import { useState, useEffect, useRef } from 'react';
import SharedDialog from '../../../../../global/Modal/Modal';
import { getSourceList } from '../../helpers/sourceListHelpers';
import { searchContext, userAppContext } from '../../../../../context/Context';
import { SearchContext, UserContext } from '../../../../../types';
import { XMarkIcon } from '@heroicons/react/16/solid';
import { getFilters } from '../../../../../services/savedFiltersService';

interface ScoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyWeights?: (weights: SelectedSource[]) => void;
  onScoreSaved?: () => void;
  refreshTrigger?: number;
}

interface SelectedSource {
  name: string;
  score: number;
}

interface SavedScore {
  id?: string;
  key?: string;
  name: string;
  sourceWeights: Array<{ name: string; score: number }>;
  savedAt: string;
  searchContext?: {
    view: string;
    tab: string;
  };
}

export default function ScoreModal({
  isOpen,
  onClose,
  onApplyWeights,
  onScoreSaved,
  refreshTrigger = 0,
}: ScoreModalProps) {
  const { tab, restrictSources } = searchContext() as unknown as SearchContext;
  const { isAuthenticated, user } = userAppContext() as unknown as UserContext;
  const isTrial = Array.isArray(user?.['cognito:groups']) && user['cognito:groups'].includes('ROLE_TRIAL');
  const [availableSources, setAvailableSources] = useState<string[]>([]);
  const [selectedSources, setSelectedSources] = useState<SelectedSource[]>(() => {
    // Load existing weights from sessionStorage for current tab
    const stored = sessionStorage.getItem(`sourceWeights_${tab}`);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        return [];
      }
    }
    return [];
  });
  const [showWarning, setShowWarning] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<'configure' | 'saved'>('configure');
  const [savedScores, setSavedScores] = useState<SavedScore[]>([]);

  useEffect(() => {
    if (isTrial && activeTab === 'saved') {
      setActiveTab('configure');
    }
  }, [isTrial, activeTab]);

  useEffect(() => {
    const sourceList = getSourceList({ tab, restrictSources });
    let sources: string[] = [];

    if (Array.isArray(sourceList)) {
      sources = sourceList;
    } else {
      sources = Object.values(sourceList).flat();
    }

    const filteredSources = sources.filter(source => source !== 'ALL');
    setAvailableSources(filteredSources);

    const stored = sessionStorage.getItem(`sourceWeights_${tab}`);
    if (stored) {
      try {
        setSelectedSources(JSON.parse(stored));
      } catch (e) {
        setSelectedSources([]);
      }
    } else {
      setSelectedSources([]);
    }

    loadSavedScores();
  }, [tab, restrictSources]);

  useEffect(() => {
    if (isOpen) {
      loadSavedScores();
    }
  }, [isOpen, refreshTrigger]);

  const loadSavedScores = async () => {
    const scores: SavedScore[] = [];

    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('savedScore_')) {
        try {
          const score = JSON.parse(localStorage.getItem(key) || '');
          scores.push({
            key,
            ...score,
          });
        } catch (error) {
          console.error('Error parsing saved score:', error);
        }
      }
    });

    if (isAuthenticated) {
      try {
        const response = await getFilters();
        if (response.filters && Array.isArray(response.filters)) {
          const scoreFilters = response.filters
            .filter(f => f.type === 'score')
            .map(f => ({
              id: f.id,
              name: f.name,
              sourceWeights: f.sourceWeights,
              savedAt: f.createdAt || f.savedAt,
              searchContext: f.searchContext,
            }));
          scores.push(...scoreFilters);
        }
      } catch (error) {
        console.error('Error loading saved scores from DynamoDB:', error);
      }
    }

    const filteredScores = scores.filter(score => {
      if (!score.searchContext) return true;
      return score.searchContext.tab === tab;
    });

    filteredScores.sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());

    setSavedScores(filteredScores);
  };

  const handleLoadSavedScore = (score: SavedScore) => {
    if (score.sourceWeights && score.sourceWeights.length > 0) {
      setSelectedSources(score.sourceWeights);
      setActiveTab('configure');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Utility function to handle floating-point precision issues
  const roundToDecimal = (num: number, decimals: number = 2) => {
    return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
  };

  const getTotalScore = () => {
    const total = selectedSources.reduce((sum, source) => sum + source.score, 0);
    return roundToDecimal(total);
  };

  const handleAddSource = (sourceName: string) => {
    if (selectedSources.find(s => s.name === sourceName)) {
      return;
    }

    const currentTotal = getTotalScore();
    const remainingScore = roundToDecimal(1 - currentTotal);

    if (remainingScore <= 0) {
      // If no remaining score, add with 0 and show warning
      setSelectedSources([...selectedSources, { name: sourceName, score: 0 }]);
      setShowWarning(true);
      return;
    }

    // Add source with remaining score or a default small value
    const defaultScore = roundToDecimal(Math.min(0.05, remainingScore));
    setSelectedSources([...selectedSources, { name: sourceName, score: defaultScore }]);
    setShowWarning(false);
  };

  const handleRemoveSource = (sourceName: string) => {
    setSelectedSources(selectedSources.filter(s => s.name !== sourceName));
    setShowWarning(false);
  };

  const handleScoreChange = (sourceName: string, newScore: number) => {
    const otherSourcesTotal = selectedSources
      .filter(s => s.name !== sourceName)
      .reduce((sum, s) => sum + s.score, 0);

    const maxAllowedScore = roundToDecimal(1 - otherSourcesTotal);

    // Allow the change if it doesn't exceed the maximum allowed score
    if (newScore > maxAllowedScore) {
      // If user is trying to exceed the limit, set to maximum allowed
      const adjustedScore = Math.max(0, maxAllowedScore);
      setSelectedSources(
        selectedSources.map(s =>
          s.name === sourceName ? { ...s, score: roundToDecimal(adjustedScore) } : s,
        ),
      );

      if (adjustedScore < newScore) {
        setShowWarning(true);
      } else {
        setShowWarning(false);
      }
      return;
    }

    setSelectedSources(
      selectedSources.map(s => (s.name === sourceName ? { ...s, score: roundToDecimal(newScore) } : s)),
    );
    setShowWarning(false);
  };

  const handleClear = () => {
    setSelectedSources([]);
    sessionStorage.removeItem(`sourceWeights_${tab}`);
    if (onApplyWeights) {
      onApplyWeights([]);
    }
    onClose();
    window.location.reload();
  };

  const handleApply = () => {
    if (selectedSources.length > 0) {
      sessionStorage.setItem(`sourceWeights_${tab}`, JSON.stringify(selectedSources));
    } else {
      sessionStorage.removeItem(`sourceWeights_${tab}`);
    }

    const urlParams = new URLSearchParams(window.location.search);

    if (selectedSources.length > 1) {
      urlParams.set('source', 'ALL');
    } else if (selectedSources.length === 1) {
      urlParams.set('source', selectedSources[0].name);
    } else {
      // No sources selected - keep current source or default
      // Don't change the source parameter
    }

    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.pushState({}, '', newUrl);

    // Apply weights through callback
    if (onApplyWeights) {
      onApplyWeights(selectedSources);
    }

    onClose();

    // Trigger page reload to apply changes
    window.location.reload();
  };

  const totalScore = getTotalScore();
  const isMaxReached = totalScore >= 1;

  return (
    <SharedDialog
      isOpen={isOpen}
      onClose={onClose}
      title="My Score"
      confirmText={activeTab === 'configure' ? 'Apply' : undefined}
      hasConfirmBtn={activeTab === 'configure'}
      cancelText="Close"
      onConfirm={activeTab === 'configure' ? handleApply : undefined}
      className="score-modal"
    >
      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          onClick={() => setActiveTab('configure')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'configure'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Configure
        </button>
        {!isTrial && (
          <button
            onClick={() => setActiveTab('saved')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'saved'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Saved Scores ({savedScores.length})
          </button>
        )}
      </div>

      {/* Configure Tab */}
      {activeTab === 'configure' && (
        <>
          {selectedSources.length > 0 && (
            <div className="mb-3 text-right">
              <button onClick={handleClear} className="text-sm text-red-600 hover:text-red-800 underline">
                Clear All Weights
              </button>
            </div>
          )}
          <div className="space-y-4">
            <div ref={dropdownRef} className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Sources</label>
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                disabled={isMaxReached}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-left focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed flex items-center justify-between"
              >
                <span className="text-gray-700">
                  {selectedSources.length > 0
                    ? `${selectedSources.length} source${selectedSources.length > 1 ? 's' : ''} selected`
                    : 'Choose sources...'}
                </span>
                <svg
                  className={`w-5 h-5 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  {availableSources
                    .filter(source => !selectedSources.find(s => s.name === source))
                    .map(source => (
                      <div
                        key={source}
                        onClick={() => {
                          handleAddSource(source);
                        }}
                        className="px-3 py-2 hover:bg-blue-50 cursor-pointer transition-colors"
                      >
                        <span className="text-gray-900">{source.replace('_', ' ')}</span>
                      </div>
                    ))}
                  {availableSources.filter(source => !selectedSources.find(s => s.name === source)).length ===
                    0 && <div className="px-3 py-2 text-gray-500 text-sm">All sources selected</div>}
                </div>
              )}
            </div>

            {/* Warning Message */}
            {showWarning && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-sm text-yellow-800">
                  ⚠️ The total score cannot exceed 1.0. The score has been adjusted to the maximum allowed
                  value. You can redistribute scores among sources as needed.
                </p>
              </div>
            )}

            {/* Total Score Indicator */}
            <div className="bg-gray-50 rounded-md p-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Total Score:</span>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-lg font-bold ${
                      totalScore > 1 ? 'text-red-600' : totalScore === 1 ? 'text-green-600' : 'text-gray-900'
                    }`}
                  >
                    {totalScore.toFixed(2)} / 1.00
                  </span>
                  {selectedSources.length > 1 && totalScore !== 1 && (
                    <button
                      onClick={() => {
                        const equalScore = roundToDecimal(1 / selectedSources.length);
                        setSelectedSources(selectedSources.map(s => ({ ...s, score: equalScore })));
                        setShowWarning(false);
                      }}
                      className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                      title="Distribute scores equally among all sources"
                    >
                      Distribute Equally
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    totalScore > 1 ? 'bg-red-500' : totalScore === 1 ? 'bg-green-500' : 'bg-blue-500'
                  }`}
                  style={{ width: `${Math.min(totalScore * 100, 100)}%` }}
                />
              </div>
            </div>

            {/* Selected Sources with Sliders */}
            <div className="space-y-3">
              {selectedSources.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  No sources selected yet. Choose sources from the dropdown above.
                </p>
              ) : (
                selectedSources.map(source => (
                  <div key={source.name} className="bg-white border border-gray-200 rounded-md p-3 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">{source.name.replace('_', ' ')}</span>
                      <button
                        onClick={() => handleRemoveSource(source.name)}
                        className="text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.05}
                        value={source.score}
                        onChange={e => handleScoreChange(source.name, parseFloat(e.target.value))}
                        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <input
                        type="number"
                        min={0}
                        max={1}
                        step={0.05}
                        value={source.score.toFixed(2)}
                        onChange={e => {
                          const value = parseFloat(e.target.value);
                          if (!isNaN(value)) {
                            handleScoreChange(source.name, value);
                          }
                        }}
                        className="w-20 px-2 py-1 text-sm border border-gray-300 rounded text-center"
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}

      {/* Saved Scores Tab */}
      {activeTab === 'saved' && !isTrial && (
        <div className="space-y-3">
          {savedScores.length === 0 && !isTrial ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-2">No saved scores yet</p>
              <p className="text-sm text-gray-400">Configure and save a score to see it here</p>
              <button
                onClick={() => setActiveTab('configure')}
                className="mt-4 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Configure New Score
              </button>
            </div>
          ) : savedScores.length > 0 ? (
            savedScores.map((score, index) => (
              <div
                key={score.id || score.key || index}
                className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-base font-semibold text-gray-900 mb-1">{score.name}</h4>
                    <p className="text-xs text-gray-500 mb-3">
                      Saved: {new Date(score.savedAt).toLocaleDateString()} at{' '}
                      {new Date(score.savedAt).toLocaleTimeString()}
                    </p>
                    {score.sourceWeights && score.sourceWeights.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-gray-700 mb-2">Source Weights:</p>
                        {score.sourceWeights.map((sw, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between bg-gray-50 rounded px-3 py-2"
                          >
                            <span className="text-sm text-gray-700">{sw.name.replace('_', ' ')}</span>
                            <span className="text-sm font-semibold text-gray-900">{sw.score.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => handleLoadSavedScore(score)}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Load
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : null}
        </div>
      )}
    </SharedDialog>
  );
}
